import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Globe2, LayoutDashboard, Loader2, LogOut, MessageSquare } from "lucide-react";
import { UnreadBadge, useUnread } from "@/components/messages/unread";
import { useDevAuth } from "@/context/DevAuthContext";
import { DEV_SPACE, SpaceProvider } from "@/context/SpaceContext";
import { DriveProvider } from "@/context/DriveContext";
import { useI18n } from "@/i18n/I18nContext";
import { HealBoundary } from "@/components/selfheal/HealBoundary";

// Developer space: never behind the visitor queue; same catalog components as the admin, scoped to /api/dev.
export const DevShell = () => {
  const { dev, logout } = useDevAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const unread = useUnread("/dev/messages/unread", Boolean(dev));
  if (dev === null) {
    return <div className="flex min-h-[60vh] items-center justify-center" data-testid="dev-auth-checking"><Loader2 className="h-8 w-8 animate-spin text-[var(--gold)]" /></div>;
  }
  if (!dev) {
    if (location.pathname !== "/espace-developpeur") sessionStorage.setItem("fmo_after_login", location.pathname + location.search);
    return <Navigate to="/espace-developpeur/connexion" replace />;
  }
  const out = async () => {
    await logout();
    navigate("/espace-developpeur/connexion", { replace: true });
  };
  return (
    <SpaceProvider value={DEV_SPACE}>
      <div data-testid="dev-shell">
        <div className="border-b border-amber-400/25 bg-[rgba(40,28,4,0.6)]">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2.5 sm:px-8">
            <div className="flex flex-wrap items-center gap-4">
              <NavLink to="/espace-developpeur" end className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[var(--gold-bright)]" data-testid="dev-nav-dashboard">
                <LayoutDashboard className="h-4 w-4" /> {t("dev.dashboard")}
              </NavLink>
              <NavLink to="/espace-developpeur/messages" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-200 hover:text-[var(--gold-bright)]" data-testid="dev-nav-messages">
                <MessageSquare className="h-4 w-4" /> {t("msg.title")} <UnreadBadge count={unread} testId="dev-messages-unread" />
              </NavLink>
              <NavLink to="/apps" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-200 hover:text-[var(--gold-bright)]" data-testid="dev-nav-view-site">
                <Globe2 className="h-4 w-4" /> {t("dev.viewSite")}
              </NavLink>
            </div>
            <div className="flex min-w-0 items-center gap-3">
              <span className="hidden truncate text-xs text-neutral-300 sm:inline" data-testid="dev-user-email">{dev.brand} · {dev.email}</span>
              <button type="button" onClick={out} className="btn-red btn-sm" data-testid="dev-logout-btn">
                <LogOut className="h-4 w-4 rtl:-scale-x-100" /> {t("admin.logout")}
              </button>
            </div>
          </div>
        </div>
        <HealBoundary name="dev-section" resetKey={location.pathname}>
          <DriveProvider><Outlet /></DriveProvider>
        </HealBoundary>
      </div>
    </SpaceProvider>
  );
};
